import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Textarea } from './ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Check, X, Clock, ShieldCheck, Package, Building2, AlertTriangle,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// ─── Approval Queue (Super Admin) ───
export function ApprovalQueue({ token }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const fetch = () => {
    setLoading(true);
    axios.get(`${BACKEND_URL}/api/admin/approval-queue`, { headers })
      .then(res => setItems(res.data.items || []))
      .catch(() => {}).finally(() => setLoading(false));
  };
  useEffect(() => { fetch(); }, []);

  const process = async (id, action) => {
    try {
      await axios.put(`${BACKEND_URL}/api/admin/approval-queue/${id}?action=${action}`, {}, { headers });
      toast.success(action === 'approve' ? 'Approved and applied' : 'Rejected');
      fetch();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-3xl mx-auto p-6 space-y-6" data-testid="approval-queue">
      <div>
        <h2 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>Approval Queue</h2>
        <p className="text-sm text-muted-foreground">Vendor and brand portal submissions awaiting review.</p>
      </div>
      {loading ? <div className="py-12 text-center"><div className="animate-spin h-5 w-5 border-2 border-primary border-t-transparent rounded-full mx-auto" /></div> :
      items.length === 0 ? (
        <div className="py-12 text-center border rounded-xl">
          <ShieldCheck className="h-8 w-8 text-muted-foreground/25 mx-auto mb-3" />
          <p className="text-sm text-muted-foreground">No pending submissions.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map(item => (
            <div key={item.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${item.source === 'vendor' ? 'bg-amber-100' : 'bg-purple-100'}`}>
                {item.source === 'vendor' ? <Package className="h-4 w-4 text-amber-700" /> : <Building2 className="h-4 w-4 text-purple-700" />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium">{item.entity_type.replace('_', ' ')}</div>
                <div className="text-xs text-muted-foreground">From {item.source} · {new Date(item.created_at).toLocaleDateString()}</div>
              </div>
              <Badge variant="outline" className={`text-[10px] ${item.status === 'pending' ? 'text-amber-600' : item.status === 'approve' ? 'text-emerald-600' : 'text-red-600'}`}>
                {item.status}
              </Badge>
              {item.status === 'pending' && (
                <div className="flex gap-1">
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-emerald-600" onClick={() => process(item.id, 'approve')}>
                    <Check className="h-3 w-3" /> Approve
                  </Button>
                  <Button size="sm" variant="outline" className="h-7 text-xs gap-1 text-red-600" onClick={() => process(item.id, 'reject')}>
                    <X className="h-3 w-3" /> Reject
                  </Button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Vendor Portal Page ───
export function VendorPortalPage() {
  const [token, setToken] = useState(null);
  const [vendorId, setVendorId] = useState('');
  const [products, setProducts] = useState([]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const headers = useMemo(() => token ? { Authorization: `Bearer ${token}` } : {}, [token]);

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${BACKEND_URL}/api/vendor-portal/login`, { email, password });
      setToken(res.data.token);
      setVendorId(res.data.vendor_id);
    } catch { setError('Invalid credentials'); }
  };

  useEffect(() => {
    if (!token) return;
    axios.get(`${BACKEND_URL}/api/vendor-portal/my-products`, { headers })
      .then(res => setProducts(res.data.products || [])).catch(() => {});
  }, [token, headers]);

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Vendor Portal</h1>
        <p className="text-sm text-muted-foreground mb-6">Manage your products and integrations on LodgiFlow.</p>
        <form onSubmit={login} className="space-y-4">
          <div><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div><Label className="text-xs">Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} /></div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full">Sign In to Vendor Portal</Button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 h-14 flex items-center justify-between">
        <h1 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>LodgiFlow Vendor Portal</h1>
        <Button variant="ghost" size="sm" onClick={() => setToken(null)}>Sign Out</Button>
      </header>
      <div className="max-w-4xl mx-auto p-6 space-y-6">
        <h2 className="text-lg font-semibold">Your Products ({products.length})</h2>
        <div className="space-y-2">
          {products.map(p => (
            <div key={p.id} className="flex items-center gap-3 p-3 rounded-lg border bg-card">
              <Package className="h-4 w-4 text-muted-foreground" />
              <div className="flex-1"><div className="text-sm font-medium">{p.name}</div><div className="text-xs text-muted-foreground">{p.platform_id}</div></div>
              <Badge variant="outline" className="text-[10px]">{p.status}</Badge>
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground">To update product information or submit new integrations, contact your LodgiFlow administrator or use the submission form below.</p>
      </div>
    </div>
  );
}

// ─── Brand Portal Page ───
export function BrandPortalPage() {
  const [token, setToken] = useState(null);
  const [brandId, setBrandId] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');

  const login = async (e) => {
    e.preventDefault();
    try {
      const res = await axios.post(`${BACKEND_URL}/api/brand-portal/login`, { email, password });
      setToken(res.data.token);
      setBrandId(res.data.brand_id);
    } catch { setError('Invalid credentials'); }
  };

  if (!token) return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6">
      <div className="w-full max-w-sm">
        <h1 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk' }}>Brand Portal</h1>
        <p className="text-sm text-muted-foreground mb-6">Define technology standards for your hotel brand.</p>
        <form onSubmit={login} className="space-y-4">
          <div><Label className="text-xs">Email</Label><Input type="email" value={email} onChange={e => setEmail(e.target.value)} /></div>
          <div><Label className="text-xs">Password</Label><Input type="password" value={password} onChange={e => setPassword(e.target.value)} /></div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <Button type="submit" className="w-full">Sign In to Brand Portal</Button>
        </form>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b px-6 h-14 flex items-center justify-between">
        <h1 className="text-lg font-bold" style={{ fontFamily: 'Space Grotesk' }}>LodgiFlow Brand Portal</h1>
        <Button variant="ghost" size="sm" onClick={() => setToken(null)}>Sign Out</Button>
      </header>
      <div className="max-w-4xl mx-auto p-6">
        <p className="text-sm text-muted-foreground">Brand portal functionality coming soon. Contact LodgiFlow to define your brand's technology standards.</p>
      </div>
    </div>
  );
}
